// Typed block union for article content. Persisted as JSON in `articles.body`.
//
// Each article body is an ordered array of these blocks. Adding a new
// variation = one new union case here + one renderer in
// app/src/components/article/. No row-shape change.
//
// Phase 4 of v2 redesign — see docs/redesign/BRIEF.md §3.4 and ADR-0002.

import { z } from 'zod';

// Quiz question — lifted here from app/src/types/article.ts so it's Zod-validated
// at the API boundary instead of only typed at the TS level (which is how it
// silently slipped through the v1 seed).
export const quizQuestionSchema = z.object({
  question: z.string().min(1),
  options: z.array(z.string()).min(2),
  answer: z.number().int().nonnegative(),
});

export const calloutVariantSchema = z.enum(['info', 'warn', 'success', 'quote']);
export const embedProviderSchema = z.enum(['youtube', 'vimeo', 'generic']);

export const articleBlockSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('text'),
    body: z.string(),
  }),
  z.object({
    type: z.literal('heading'),
    level: z.union([z.literal(2), z.literal(3)]),
    text: z.string().min(1),
  }),
  z.object({
    type: z.literal('image'),
    mediaId: z.string().optional(),
    src: z.string().min(1),
    alt: z.string(),
    caption: z.string().optional(),
  }),
  z.object({
    type: z.literal('quiz'),
    questions: z.array(quizQuestionSchema).min(1),
  }),
  z.object({
    type: z.literal('author'),
    name: z.string().min(1),
    bio: z.string().optional(),
    avatar: z.string().optional(),
    href: z.string().optional(),
  }),
  z.object({
    type: z.literal('callout'),
    variant: calloutVariantSchema,
    body: z.string().min(1),
  }),
  z.object({
    type: z.literal('code'),
    lang: z.string().optional(),
    body: z.string(),
  }),
  z.object({
    type: z.literal('embed'),
    provider: embedProviderSchema,
    url: z.string().min(1),
  }),
]);

export type ArticleBlock = z.infer<typeof articleBlockSchema>;
export type QuizQuestion = z.infer<typeof quizQuestionSchema>;
export type CalloutVariant = z.infer<typeof calloutVariantSchema>;
export type EmbedProvider = z.infer<typeof embedProviderSchema>;

// Narrow helpers so renderers / editors can pull a single variant by type.
export type ArticleBlockOf<K extends ArticleBlock['type']> = Extract<ArticleBlock, { type: K }>;
